/* Tela 6 · Empresas e cobertura (PDD seção 3): municípios consultados no ADN, a carteira
   por situação e a tabela de emitentes. Cada linha abre as verificações RN14/RN15 e o
   atalho para resolver. */
import React, { useMemo, useState } from 'react';
import { Box, Flex, Grid, Input, InputGroup, InputLeftElement, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Buildings, FilePlus, MagnifyingGlass, MapPin, UploadSimple } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { useAuth } from '../../../../../providers/auth';
import { INfseEmitente, NfseEmitenteSituacao } from '../../../../../types/nfseEmissao';
import {
  certificadosPath,
  emitirPath,
  NFSE_ACOES,
  NFSE_EMISSAO_PATH,
  NFSE_PATHS,
} from '../shared/constants';
import { Carregando, NfsePage } from '../shared/NfsePage';
import { Linha, SubLinha, Tabela, Td, Th, Vazio } from '../shared/tabela';
import {
  Banner,
  C,
  Card,
  CardHead,
  CheckItem,
  CheckList,
  Chip,
  cnpjMask,
  dataBR,
  EnvBadge,
  Meter,
  ModuleHeader,
  NButton,
  NfseDrawer,
  Ring,
  StatusTag,
} from '../shared/ui';

type Filtro = 'todas' | 'aptas' | 'bloq';

const ADESAO_TEXTO = {
  emissor: 'Emissor Nacional',
  ambiente: 'só Ambiente Nacional',
  nenhuma: 'sem convênio',
};

const SITUACOES: [NfseEmitenteSituacao, string, string][] = [
  ['liberado', 'Podem emitir', C.success],
  ['municipio_nao_aderente', 'Município não aderente', C.warning],
  ['certificado_vencido', 'Certificado vencido', C.danger],
  ['procuracao_vencida', 'Procuração vencida', C.danger],
  ['sem_certificado', 'Sem certificado', C.placeholder],
  ['sem_ibge', 'Sem código IBGE', C.placeholder],
];

const num = (v: number) => v.toLocaleString('pt-BR');

function ufConfere(e: INfseEmitente): CheckItem['state'] {
  if (!e.codigo_ibge) return 'pending';
  return e.situacao === 'sem_ibge' ? 'fail' : 'ok';
}

function verificacoes(e: INfseEmitente): CheckItem[] {
  let adesao: CheckItem['state'] = 'pending';
  if (e.adesao === 'emissor') adesao = 'ok';
  if (e.adesao === 'ambiente') adesao = e.situacao === 'municipio_nao_aderente' ? 'fail' : 'warn';
  if (e.adesao === 'nenhuma') adesao = 'fail';
  const cert = { valido: 'ok', a_vencer: 'warn', vencido: 'fail', ausente: 'fail' } as const;
  return [
    {
      title: 'Código IBGE no cadastro',
      state: e.codigo_ibge ? 'ok' : 'fail',
      detail: e.codigo_ibge
        ? `${e.codigo_ibge} · ${e.municipio ?? '—'}/${e.uf ?? '—'}`
        : `"${e.municipio ?? '—'}" não casou com a tabela de municípios do IBGE`,
    },
    {
      title: 'UF confere com o IBGE',
      state: ufConfere(e),
      detail: e.codigo_ibge
        ? `prefixo ${e.codigo_ibge.slice(0, 2)} · ${e.uf ?? '—'}`
        : 'depende do código',
    },
    {
      title: 'Adesão no ambiente',
      state: adesao,
      detail:
        {
          emissor: 'aderenteEmissorNacional = 1',
          ambiente: 'só aderenteAmbienteNacional = 1',
          nenhuma: 'sem convênio no ADN',
        }[e.adesao ?? ''] ?? 'ainda não consultado no ADN',
    },
    {
      title: 'Certificado A1',
      state: cert[e.certificado_situacao],
      detail: e.certificado_valido_ate
        ? `${e.certificado_emissor ?? 'ICP-Brasil'} · até ${dataBR(e.certificado_valido_ate)}`
        : 'não enviado',
    },
  ];
}

function Detalhe({
  emitente,
  onClose,
  podeEmitir,
  podeCertificado,
}: {
  emitente: INfseEmitente | null;
  onClose: () => void;
  podeEmitir: boolean;
  podeCertificado: boolean;
}) {
  const navigate = useNavigate();
  const e = emitente;
  const certificado =
    e && ['sem_certificado', 'certificado_vencido', 'procuracao_vencida'].includes(e.situacao);
  return (
    <NfseDrawer
      isOpen={!!e}
      onClose={onClose}
      size="md"
      icon={Buildings}
      tone={e?.situacao === 'liberado' ? 'green' : 'warning'}
      title={e?.razao_social}
      subtitle={cnpjMask(e?.cnpj)}
      footer={
        e && (
          <>
            {e.situacao === 'liberado' && podeEmitir && (
              <NButton icon={FilePlus} onClick={() => navigate(emitirPath(e.id))}>
                Emitir NFS-e
              </NButton>
            )}
            {certificado && podeCertificado && (
              <NButton icon={UploadSimple} onClick={() => navigate(certificadosPath(e.id))}>
                Enviar certificado
              </NButton>
            )}
            {e.situacao !== 'liberado' && !certificado && (
              <NButton variant="secondary" onClick={onClose}>
                Fechar
              </NButton>
            )}
          </>
        )
      }
    >
      {e && (
        <Flex direction="column" gap={4}>
          <Box>
            <StatusTag kind={e.situacao} />
          </Box>
          <CheckList items={verificacoes(e)} />
          {e.situacao === 'liberado' ? (
            <Banner tone="success" compact>
              Pronta para emitir. Próxima DPS: série {e.serie}, nº {e.proximo_n_dps}.
            </Banner>
          ) : (
            <Banner tone="warning" compact>
              {e.motivo_bloqueio}
            </Banner>
          )}
        </Flex>
      )}
    </NfseDrawer>
  );
}

export function NfseEmissaoEmpresasPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { getPainel, getEmitentes } = useNfseEmissao();
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [busca, setBusca] = useState('');
  const [selecionada, setSelecionada] = useState<INfseEmitente | null>(null);

  const painel = useQuery({ queryKey: ['nfse-painel', 'mes'], queryFn: getPainel }).data;
  const emitentesQuery = useQuery({ queryKey: ['nfse-emitentes'], queryFn: getEmitentes });
  const emitentes = emitentesQuery.data ?? [];

  const podeEmitir = hasPermission(NFSE_EMISSAO_PATH, NFSE_ACOES.emitir);
  const podeCertificado = hasPermission(NFSE_EMISSAO_PATH, NFSE_ACOES.certificado);

  const aptas = emitentes.filter(e => e.situacao === 'liberado').length;
  const linhas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const digitos = termo.replace(/\D/g, '');
    return emitentes.filter(e => {
      if (filtro === 'aptas' && e.situacao !== 'liberado') return false;
      if (filtro === 'bloq' && e.situacao === 'liberado') return false;
      if (!termo) return true;
      return (
        e.razao_social.toLowerCase().includes(termo) ||
        (digitos.length >= 3 && e.cnpj.includes(digitos)) ||
        (e.municipio ?? '').toLowerCase().includes(termo)
      );
    });
  }, [emitentes, filtro, busca]);

  const cob = painel?.cobertura;
  const total = cob?.consultados || 1;
  const somaComAmbiente = (cob?.emissor_nacional ?? 0) + (cob?.so_ambiente_nacional ?? 0);

  return (
    <NfsePage wide ambiente={painel?.ambiente}>
      <ModuleHeader
        title="Empresas e cobertura"
        icon={MapPin}
        tone="indigo"
        description="A tela não depende de lista feita à mão: cruza empresa → código IBGE (nossa base) com IBGE → adesão (ADN). Cada empresa bloqueada mostra o motivo e o atalho para resolver."
        crumbs={[
          { label: 'Emissão de NFS-e', to: NFSE_PATHS.painel },
          { label: 'Empresas e cobertura' },
        ]}
        actions={<EnvBadge ambiente={painel?.ambiente} />}
      />

      <Grid templateColumns={{ base: '1fr', lg: 'minmax(0, 1.5fr) minmax(0, 1fr)' }} gap="18px">
        <Card p={6}>
          <CardHead
            title="Municípios no Ambiente Nacional"
            description={
              cob?.atualizados_em
                ? `Consulta ao /parametrizacao/{IBGE}/convenio em ${dataBR(cob.atualizados_em)} · atualiza toda semana`
                : 'Ainda sem consulta ao ADN neste ambiente'
            }
          />
          {cob && cob.consultados > 0 ? (
            <>
              <Flex
                h="14px"
                borderRadius="999px"
                overflow="hidden"
                gap="2px"
                role="img"
                aria-label={`${cob.emissor_nacional} aderentes ao Emissor Nacional, ${cob.so_ambiente_nacional} só no Ambiente Nacional, ${cob.sem_convenio} sem convênio, de ${cob.consultados}`}
              >
                <Box w={`${(cob.emissor_nacional / total) * 100}%`} bg={C.brand700} />
                <Box w={`${(cob.so_ambiente_nacional / total) * 100}%`} bg="#7DC4EC" />
                <Box w={`${Math.max(0.8, (cob.sem_convenio / total) * 100)}%`} bg={C.warning} />
              </Flex>
              <Flex gap={5} mt={3} wrap="wrap" fontSize="12.5px" color={C.muted}>
                {[
                  [C.brand700, cob.emissor_nacional, 'aderentes ao Emissor Nacional'],
                  ['#7DC4EC', cob.so_ambiente_nacional, 'só no Ambiente Nacional'],
                  [C.warning, cob.sem_convenio, 'sem convênio'],
                ].map(([cor, n, rotulo]) => (
                  <Flex key={rotulo as string} align="center" gap={2}>
                    <Box w="9px" h="9px" borderRadius="3px" bg={cor as string} />
                    <Text>
                      <Text as="b" color={C.ink}>
                        {num(n as number)}
                      </Text>{' '}
                      {rotulo}
                    </Text>
                  </Flex>
                ))}
              </Flex>
              {cob.flag_liberacao === 'aderente_emissor_nacional' &&
                cob.so_ambiente_nacional > 0 && (
                  <Box mt={4}>
                    <Banner tone="info" compact>
                      Municípios só no Ambiente Nacional (como Goiânia) ainda não liberam a emissão.
                      Se a emissão real em produção passar, a regra troca de flag e a cobertura sobe
                      de <b>{num(cob.emissor_nacional)}</b> para <b>{num(somaComAmbiente)}</b>{' '}
                      municípios, sem deploy.
                    </Banner>
                  </Box>
                )}
            </>
          ) : (
            <Text fontSize="13px" color={C.subtle}>
              O job semanal ainda não gravou a adesão dos municípios. Os municípios da carteira são
              consultados na hora, ao abrir esta tela.
            </Text>
          )}
        </Card>

        <Card p={6}>
          <CardHead
            title="Sua carteira"
            description={`${emitentes.length} empresa(s) habilitada(s) como emitentes`}
            right={
              <Ring
                value={emitentes.length ? (aptas / emitentes.length) * 100 : 0}
                label="Podem emitir"
              />
            }
          />
          <Flex direction="column" gap="10px">
            {SITUACOES.map(([chave, rotulo, cor]) => {
              const n = emitentes.filter(e => e.situacao === chave).length;
              return (
                <Flex key={chave} align="center" gap={2.5} fontSize="13px">
                  <Text w="160px" color={C.ink2}>
                    {rotulo}
                  </Text>
                  <Box flex={1}>
                    <Meter
                      value={emitentes.length ? (n / emitentes.length) * 100 : 0}
                      color={cor}
                    />
                  </Box>
                  <Text as="b" w="28px" textAlign="right">
                    {n}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </Card>
      </Grid>

      <Card overflow="hidden">
        <Flex
          p="16px 20px"
          gap={2}
          align="center"
          wrap="wrap"
          borderBottomWidth="1px"
          borderColor={C.line}
        >
          <Text as="h2" fontSize="16px" fontWeight={650} mr="auto">
            Empresas
          </Text>
          <Chip
            selected={filtro === 'todas'}
            count={emitentes.length}
            onClick={() => setFiltro('todas')}
          >
            Todas
          </Chip>
          <Chip selected={filtro === 'aptas'} count={aptas} onClick={() => setFiltro('aptas')}>
            Podem emitir
          </Chip>
          <Chip
            selected={filtro === 'bloq'}
            count={emitentes.length - aptas}
            onClick={() => setFiltro('bloq')}
          >
            Bloqueadas
          </Chip>
          <InputGroup size="sm" w="220px">
            <InputLeftElement pointerEvents="none">
              <MagnifyingGlass size={15} color={C.subtle} />
            </InputLeftElement>
            <Input
              aria-label="Buscar empresa"
              placeholder="Nome, CNPJ ou município"
              value={busca}
              onChange={ev => setBusca(ev.target.value)}
              borderRadius="999px"
              bg="rgba(255, 255, 255, 0.78)"
              borderColor={C.lineInput}
              h="32px"
            />
          </InputGroup>
        </Flex>
        {emitentesQuery.isLoading ? (
          <Carregando texto="Conferindo município e certificado de cada empresa…" />
        ) : (
          <Tabela
            cabecalho={
              <>
                <Th>Empresa</Th>
                <Th>Município · adesão</Th>
                <Th>Certificado</Th>
                <Th>Última nota</Th>
                <Th>Situação</Th>
                <Th />
              </>
            }
          >
            {linhas.map((e, i) => {
              const cs = e.certificado_situacao;
              let certCor: string | undefined;
              if (cs === 'vencido') certCor = C.danger;
              if (cs === 'a_vencer') certCor = C.warning;
              let certTexto = `até ${dataBR(e.certificado_valido_ate)}`;
              if (cs === 'vencido') certTexto = `venceu ${dataBR(e.certificado_valido_ate)}`;
              if (cs === 'a_vencer') certTexto = `vence em ${e.certificado_dias} dias`;
              return (
                <Linha
                  key={e.id}
                  i={i}
                  selecionada={selecionada?.id === e.id}
                  onClick={() => setSelecionada(e)}
                >
                  <Td>
                    <Text fontWeight={600} noOfLines={1}>
                      {e.razao_social}
                    </Text>
                    <SubLinha>{cnpjMask(e.cnpj)}</SubLinha>
                  </Td>
                  <Td>
                    <Text color={e.codigo_ibge ? undefined : C.subtle}>
                      {e.municipio ?? '—'}/{e.uf ?? '—'}
                    </Text>
                    <SubLinha>
                      {e.codigo_ibge
                        ? `IBGE ${e.codigo_ibge} · ${e.adesao ? ADESAO_TEXTO[e.adesao] : 'não consultado'}`
                        : 'sem correspondência no IBGE'}
                    </SubLinha>
                  </Td>
                  <Td color={certCor} fontWeight={certCor ? 600 : 400}>
                    {cs === 'ausente' ? <Text color={C.subtle}>não enviado</Text> : certTexto}
                  </Td>
                  <Td>
                    {e.ultima_nota
                      ? `Nº ${e.ultima_nota.n_nfse ?? e.ultima_nota.n_dps} · ${dataBR(e.ultima_nota.data)}`
                      : '—'}
                    <SubLinha>próxima: {e.proximo_n_dps}</SubLinha>
                  </Td>
                  <Td>
                    <StatusTag kind={e.situacao} />
                  </Td>
                  <Td textAlign="right">
                    {e.situacao === 'liberado' && podeEmitir ? (
                      <NButton
                        size="sm"
                        onClick={ev => {
                          ev.stopPropagation();
                          navigate(emitirPath(e.id));
                        }}
                      >
                        Emitir
                      </NButton>
                    ) : (
                      e.situacao !== 'liberado' && (
                        <NButton
                          size="sm"
                          variant="secondary"
                          onClick={ev => {
                            ev.stopPropagation();
                            setSelecionada(e);
                          }}
                        >
                          Ver motivo
                        </NButton>
                      )
                    )}
                  </Td>
                </Linha>
              );
            })}
            {linhas.length === 0 && (
              <Vazio colunas={6}>
                {emitentes.length
                  ? 'Nenhuma empresa com esses filtros.'
                  : 'Nenhuma empresa habilitada ainda. A empresa passa a emitir quando o certificado A1 dela é enviado.'}
              </Vazio>
            )}
          </Tabela>
        )}
      </Card>

      <Detalhe
        emitente={selecionada}
        onClose={() => setSelecionada(null)}
        podeEmitir={podeEmitir}
        podeCertificado={podeCertificado}
      />
    </NfsePage>
  );
}
